import { useState } from "react"

import { useTranslation } from "react-i18next"

import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Circle,
  Download,
  Eye,
  FileText,
  Info,
  Loader2,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { getReadableFileType, renderFileIcon } from "@/features/chat/components/file-picker/utils"
import { getEffectiveProjectFileStatus } from "@/features/projects/utils/project-file-status"
import { useCitationFileDownload } from "@/hooks/use-citation-file"
import { downloadBlob } from "@/lib/download"
import { cn } from "@/lib/utils"
import {
  type EmbeddingStatus,
  type EmbeddingStatusResponse,
  getProjectFileContentUrl,
  type ProjectFile,
} from "@/services/project-service"
import { useProjectFileSelectionStore } from "@/stores/project-file-selection-store"

import { ProjectFilePreviewDialog } from "./project-file-preview-dialog"

type ProjectFileListProps = {
  projectId: string
  files: ProjectFile[] | undefined
  embeddingStatus: EmbeddingStatusResponse | undefined
  isLoading?: boolean
  isBulkDeleting?: boolean
  isSelectionMode?: boolean
  selectableFilenames?: Set<string>
  onEnterSelectionMode?: (filename?: string, index?: number) => void
}

export function ProjectFileList({
  projectId,
  files,
  embeddingStatus,
  isLoading,
  isBulkDeleting,
  isSelectionMode = false,
  selectableFilenames,
  onEnterSelectionMode,
}: ProjectFileListProps) {
  const { t } = useTranslation("", { keyPrefix: "project_settings.files" })
  const { toggleFile, selectRange, isSelected, lastSelectedIndex } = useProjectFileSelectionStore()
  const downloadMutation = useCitationFileDownload()
  const [previewFilename, setPreviewFilename] = useState<string | null>(null)

  const handleDownload = (event: React.MouseEvent, filename: string) => {
    event.stopPropagation()
    downloadMutation.mutate(getProjectFileContentUrl(projectId, filename), {
      onSuccess: (blob) => downloadBlob(blob, filename),
    })
  }

  const handlePreview = (event: React.MouseEvent, filename: string) => {
    event.stopPropagation()
    setPreviewFilename(filename)
  }

  const getFileStatus = (filename: string): EmbeddingStatus => {
    const file = files?.find((candidate) => candidate.filename === filename)
    return file ? getEffectiveProjectFileStatus(file, embeddingStatus) : "completed"
  }

  const getFileError = (filename: string): string | undefined => {
    return embeddingStatus?.files.find((f) => f.filename === filename)?.error
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4">
        <Spinner className="size-4" />
        <span className="text-muted-foreground text-sm">{t("loading_files")}</span>
      </div>
    )
  }

  if (!files || files.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <FileText className="text-muted-foreground/50 mb-2 size-8" />
        <p className="text-muted-foreground text-sm">{t("no_files")}</p>
      </div>
    )
  }

  const allFilenames = files.map((file) => file.filename)

  return (
    <div className="space-y-2">
      {files.map((file, fileIndex) => {
        const status = getFileStatus(file.filename)
        const isPending = status === "pending" || status === "processing"
        const fileIsSelected = isSelected(file.filename)
        const isBeingDeleted = isBulkDeleting && fileIsSelected
        const canSelect = !isPending && !isBulkDeleting
        // Files missing from blob storage have no bytes to serve -- offering
        // download/preview would just 404. Show the needs-reupload status
        // instead (see the status-icon branch below).
        const needsReupload = file.needs_reupload === true
        const showFileActions =
          !isPending && !isBeingDeleted && !isSelectionMode && !fileIsSelected && !needsReupload

        const handleSelect = (shiftKey: boolean) => {
          if (canSelect) {
            if (isSelectionMode) {
              const shouldRangeSelect = shiftKey && lastSelectedIndex !== null
              if (shouldRangeSelect) {
                selectRange(allFilenames, lastSelectedIndex, fileIndex, selectableFilenames)
              } else {
                toggleFile(file.filename, fileIndex)
              }
            } else if (onEnterSelectionMode) {
              onEnterSelectionMode(file.filename, fileIndex)
            }
          }
        }

        const handleCardClick = (event: React.MouseEvent) => {
          handleSelect(event.shiftKey)
        }

        const cardClassName = cn(
          "group flex items-center gap-3 rounded-lg border bg-card p-3 transition-colors hover:bg-accent/50",
          fileIsSelected && "bg-primary/5 ring-1 ring-primary/50",
          isBeingDeleted && "opacity-50",
          canSelect && "cursor-pointer"
        )

        // The row is a mouse shortcut only (it holds the preview/download buttons); the
        // native select button around the status cell is the keyboard route.
        const wrapSelect = (node: React.ReactNode) =>
          canSelect ? (
            <button
              aria-label={`${t("select_file")}: ${file.filename}`}
              aria-pressed={isSelectionMode ? fileIsSelected : undefined}
              className="focus-visible:ring-ring -m-1 flex size-6 items-center justify-center rounded-sm focus-visible:ring-2 focus-visible:outline-none"
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                handleSelect(event.shiftKey)
              }}
            >
              {node}
            </button>
          ) : (
            node
          )

        return (
          <div
            key={file.filename}
            className={cardClassName}
            role="presentation"
            onClick={handleCardClick}
          >
            <div className="shrink-0">{renderFileIcon(file.filename)}</div>
            <div className="min-w-0 flex-1">
              <div className="text-foreground truncate text-sm font-medium" title={file.filename}>
                {file.filename}
              </div>
              <div className="text-muted-foreground mt-0.5 text-xs">
                <span>{getReadableFileType(file.filename)}</span>
              </div>
            </div>
            <div className="flex shrink-0 items-center justify-end gap-1">
              {showFileActions && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        aria-label={`${t("preview_file")}: ${file.filename}`}
                        className="size-7 p-0"
                        size="icon"
                        variant="ghost"
                        onClick={(event) => handlePreview(event, file.filename)}
                        onKeyDown={(event) => event.stopPropagation()}
                      >
                        <Eye className="size-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t("preview_file")}</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        aria-label={`${t("download_file")}: ${file.filename}`}
                        className="size-7 p-0"
                        size="icon"
                        variant="ghost"
                        onClick={(event) => handleDownload(event, file.filename)}
                        onKeyDown={(event) => event.stopPropagation()}
                      >
                        <Download className="size-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t("download_file")}</TooltipContent>
                  </Tooltip>
                </>
              )}
              {wrapSelect(
                (() => {
                  const error = getFileError(file.filename)
                  const isFailed = status === "failed"

                  if (isPending) {
                    return (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Loader2 className="text-muted-foreground size-4 shrink-0 animate-spin" />
                        </TooltipTrigger>
                        <TooltipContent>
                          {status === "pending" ? t("status_pending") : t("status_processing")}
                        </TooltipContent>
                      </Tooltip>
                    )
                  }

                  if (isBeingDeleted) {
                    return <Spinner className="size-4" />
                  }

                  if (isSelectionMode || fileIsSelected) {
                    return fileIsSelected ? (
                      <CheckCircle2 className="text-primary size-4" />
                    ) : (
                      <Circle className="text-muted-foreground size-4" />
                    )
                  }

                  // Determine file status: failed > needs_reupload > analysis-only
                  // > not_in_vector_store > indexed.
                  const isAnalysisOnly = file.vector_store_eligible === false
                  const inVectorStore = file.in_vector_store === true
                  const showSelectionOnHover =
                    canSelect &&
                    (inVectorStore || isAnalysisOnly || file.in_vector_store === undefined) &&
                    !isFailed &&
                    !needsReupload

                  let statusIcon: React.ReactNode
                  let statusTooltip: string

                  if (isFailed) {
                    statusIcon = <AlertCircle className="text-destructive size-4 shrink-0" />
                    statusTooltip = error || t("status_failed")
                  } else if (needsReupload) {
                    statusIcon = <AlertCircle className="text-destructive size-4 shrink-0" />
                    statusTooltip = t("status_needs_reupload")
                  } else if (isAnalysisOnly) {
                    statusIcon = <Info className="text-muted-foreground size-4 shrink-0" />
                    statusTooltip = t("status_analysis_only")
                  } else if (file.in_vector_store === false) {
                    statusIcon = <AlertTriangle className="text-warning size-4 shrink-0" />
                    statusTooltip = t("status_not_in_vector_store")
                  } else {
                    statusIcon = <CheckCircle2 className="text-success size-4 shrink-0" />
                    statusTooltip = t("status_indexed")
                  }

                  return (
                    <>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            aria-label={statusTooltip}
                            className={cn(showSelectionOnHover && "md:group-hover:hidden")}
                            role="img"
                          >
                            {statusIcon}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>{statusTooltip}</TooltipContent>
                      </Tooltip>
                      {/* Desktop hover: select circle replaces status icon */}
                      {showSelectionOnHover && (
                        <Circle className="text-muted-foreground hidden size-4 md:group-hover:block" />
                      )}
                    </>
                  )
                })()
              )}
            </div>
          </div>
        )
      })}
      <ProjectFilePreviewDialog
        filename={previewFilename}
        open={previewFilename !== null}
        projectId={projectId}
        onOpenChange={(open) => {
          if (!open) setPreviewFilename(null)
        }}
      />
    </div>
  )
}
